import React, { useEffect, useMemo, useState } from 'react';
import './ProductGlimpse.css';

/* Compact versions of the SIGNATURE widget from each product landing page,
   sized for the sign-in panel. Copy is taken from those pages verbatim,
   except Snoh Vision, which has no landing page yet. */

const CYCLE_MS = 9000;
const PHASE_AT = [800, 1700, 2600];
const DOCS_QUERY = 'which contract with ABC has the 90-day exit clause';
const TYPE_MS = 26;
/* Docs reveals late on purpose — the question has to finish typing first */
const DOCS_PHASE_AT = [
  DOCS_QUERY.length * TYPE_MS + 300,
  DOCS_QUERY.length * TYPE_MS + 1100,
  DOCS_QUERY.length * TYPE_MS + 1900,
];

const Tick = () => (
  <svg className="g-tick" viewBox="0 0 16 16" aria-hidden="true">
    <path d="M3 8.5l3.2 3.2L13 5" />
  </svg>
);

/* ---- Snoh Docs: ask in plain language, the document answers ---- */
const Docs = ({ phase, typed }) => (
  <div className="g-docs">
    <div className="g-searchbar">
      <svg className="g-search-ico" viewBox="0 0 16 16" aria-hidden="true">
        <circle cx="7" cy="7" r="5" />
        <path d="M10.8 10.8 14 14" />
      </svg>
      <span className="g-q">
        {typed}
        <i className="g-caret" />
      </span>
    </div>

    <p className={`g-scan${phase >= 1 ? ' is-on' : ''}`}>
      Matching on meaning across text, OCR layer and metadata&hellip;
    </p>

    <div className={`g-card${phase >= 2 ? ' is-on' : ''}`}>
      <span className="g-badge">PDF</span>
      <span className="g-card-text">
        <span className="g-card-title">Supply Agreement — ABC Automotive</span>
        <span className="g-card-path">/Legal/Contracts/2026/Vendors</span>
      </span>
      <span className="g-ver">v4.2</span>
    </div>

    <p className={`g-result${phase >= 3 ? ' is-on' : ''}`}>
      <Tick />
      Found in 0.31 s · 1 of 6 matches
    </p>
  </div>
);

/* ---- Snoh Fusion: the live extraction canvas ---- */
const FUSION_FIELDS = [
  ['Invoice number', 'INV-2026-04417'],
  ['Vendor GSTIN', '08AAFCS4417K1ZP'],
  ['Total incl. GST', '₹ 2,75,706.00'],
];

const Fusion = ({ phase }) => (
  <div className="g-fusion">
    <div className="g-doc">
      <span className="g-doc-title">TAX INVOICE</span>
      <span className="g-doc-sub">ABC Components Pvt. Ltd.</span>
      <span className="g-doc-bar" style={{ width: '72%' }} />
      <span className="g-doc-bar" style={{ width: '54%' }} />
      <span className="g-doc-bar" style={{ width: '38%' }} />
      <span className="g-doc-total">₹ 2,75,706.00</span>
      <span className="g-doc-sweep" />
    </div>

    <div className="g-fields">
      <p className="g-fields-top">
        <span className="g-live" />
        Extracted
        <b>{Math.min(phase, 3)} / 3</b>
      </p>
      {FUSION_FIELDS.map(([key, value], i) => (
        <span key={key} className={`g-field${phase >= i + 1 ? ' is-on' : ''}`}>
          <span className="g-field-k">{key}</span>
          <span className="g-field-v">{value}</span>
        </span>
      ))}
    </div>
  </div>
);

/* ---- Snoh Ava: a grounded, cited conversation ---- */
const Ava = ({ phase }) => (
  <div className="g-ava">
    <p className="g-msg g-msg--ask">
      What is the status of sales order 4500019823?
    </p>

    <p className={`g-check${phase >= 1 ? ' is-on' : ''}`}>
      Live SAP query · read-only service user · 2 tables read
    </p>

    <p className={`g-msg g-msg--answer${phase >= 2 ? ' is-on' : ''}`}>
      Order 4500019823 for ABC Automotive is partially delivered — 180 of
      240 units dispatched on 14 Mar.<sup>1</sup>
    </p>

    <p className={`g-cite${phase >= 3 ? ' is-on' : ''}`}>
      <span className="g-cite-n">1</span>
      SAP S/4HANA — VBAK / LIPS
    </p>
  </div>
);

/* ---- Snoh Flow: the same five steps, whatever the request ---- */
const FLOW_STEPS = ['Raised', 'Read', 'Approved', 'Posted', 'Recorded'];
const FLOW_LIT = [1, 2, 3, 5];

const Flow = ({ phase }) => {
  const lit = FLOW_LIT[Math.min(phase, 3)];

  return (
    <div className="g-flow">
      <p className="g-flow-top">
        <span className="g-live" />
        Live request
        <span className="g-run">REQ-4821</span>
      </p>

      <div className="g-req">
        <span className="g-req-title">Purchase requisition</span>
        <span className="g-req-value">₹8,40,000</span>
      </div>
      <p className="g-req-meta">
        3 quotations attached · raised by Plant 2 maintenance
      </p>

      <ol className="g-rail">
        {FLOW_STEPS.map((step, i) => (
          <li key={step} className={i < lit ? 'is-on' : ''}>
            <span className="g-rail-dot" />
            {step}
          </li>
        ))}
      </ol>
    </div>
  );
};

/* ---- Snoh Vision: no landing page yet, so this copy is a placeholder ---- */
const Vision = ({ phase }) => (
  <div className="g-vision">
    <div className="g-frame">
      <span className="g-frame-label">
        <span className="g-live" />
        Gate 2 · 30 fps
      </span>
      <span className={`g-box g-box--one${phase >= 1 ? ' is-on' : ''}`}>
        <i>Hard hat · 98.4%</i>
      </span>
      <span className={`g-box g-box--two${phase >= 2 ? ' is-on' : ''}`}>
        <i>Forklift · 96.1%</i>
      </span>
      <span className="g-frame-sweep" />
    </div>

    <p className={`g-result${phase >= 3 ? ' is-on' : ''}`}>
      <Tick />
      2 objects tracked · no PPE violation
    </p>
  </div>
);

const PRODUCTS = [
  {
    key: 'docs',
    name: 'Snoh Docs',
    does: 'Enterprise Document Management, Simplified.',
    View: Docs,
    phaseAt: DOCS_PHASE_AT,
  },
  { key: 'fusion', name: 'Snoh Fusion', does: 'Smarter Document Processing with AI.', View: Fusion },
  { key: 'ava', name: 'Snoh Ava', does: 'Your Enterprise AI Assistant.', View: Ava },
  { key: 'flow', name: 'Snoh Flow', does: 'Workflow Automation Without Complexity.', View: Flow },
  { key: 'vision', name: 'Snoh Vision', does: 'Intelligent Vision for Smarter Operations.', View: Vision },
];

const ProductGlimpse = () => {
  const [index, setIndex] = useState(0);
  const [phase, setPhase] = useState(0);
  const [typed, setTyped] = useState('');

  const reduce = useMemo(
    () =>
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    []
  );

  useEffect(() => {
    const id = setInterval(
      () => setIndex((i) => (i + 1) % PRODUCTS.length),
      CYCLE_MS
    );
    return () => clearInterval(id);
  }, []);

  // Stage the reveal inside whichever product is showing
  useEffect(() => {
    if (reduce) {
      setPhase(3);
      return undefined;
    }
    setPhase(0);
    const schedule = PRODUCTS[index].phaseAt || PHASE_AT;
    const ids = schedule.map((ms, i) => setTimeout(() => setPhase(i + 1), ms));
    return () => ids.forEach(clearTimeout);
  }, [index, reduce]);

  // The typed question is the Docs signature, so it only runs on that slide
  useEffect(() => {
    if (PRODUCTS[index].key !== 'docs') {
      setTyped('');
      return undefined;
    }
    if (reduce) {
      setTyped(DOCS_QUERY);
      return undefined;
    }
    setTyped('');
    /* Driven off elapsed time, not tick count, so a throttled tab catches up
       instead of drifting behind the reveal timers. */
    const start = performance.now();
    let raf = 0;
    const tick = () => {
      const chars = Math.min(
        DOCS_QUERY.length,
        Math.floor((performance.now() - start) / TYPE_MS)
      );
      setTyped(DOCS_QUERY.slice(0, chars));
      if (chars < DOCS_QUERY.length) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [index, reduce]);

  const current = PRODUCTS[index];
  const { View } = current;

  return (
    <div className="glimpse" aria-hidden="true">
      <p className="glimpse-head">
        <span className="glimpse-name">{current.name}</span>
        <span className="glimpse-does">{current.does}</span>
      </p>

      <div className="glimpse-stage">
        <div className="glimpse-slide" key={current.key}>
          <View phase={phase} typed={typed} />
        </div>
      </div>

      <p className="glimpse-dots">
        {PRODUCTS.map((product, i) => (
          <span
            key={product.key}
            className={`glimpse-dot${i === index ? ' is-on' : ''}`}
          />
        ))}
      </p>
    </div>
  );
};

export default ProductGlimpse;
